import { authenticatedRequest } from '@/lib/authenticatedRequest'
import { getApiUrl } from '@/lib/serverConfig'

export interface GenerationApiKeySummary {
  id: string
  /** Null for an account key (`scope: 'user'`): a `martini login` key approved "as you". */
  organizationId: string | null
  scope?: 'organization' | 'user'
  label: string
  prefix: string
  lastFour: string
  defaultProjectId: string | null
  defaultCanvasId: string | null
  createdAt: string
  lastUsedAt: string | null
  revokedAt: string | null
  /** How the key was minted: `api` here in Settings, `cli` by `martini login`, `run` by the workflow harness (hidden from this list). */
  kind?: 'api' | 'cli' | 'run'
  expiresAt?: string | null
  deviceLabel?: string | null
}

async function responseError(response: Response, fallback: string): Promise<Error> {
  const body = (await response.json().catch(() => ({}))) as { error?: string }
  return new Error(body.error || fallback)
}

export async function getGenerationApiAccess(
  organizationId: string,
): Promise<{ enabled: boolean; hasActiveKeys: boolean }> {
  const response = await authenticatedRequest(
    `${getApiUrl()}/organizations/${encodeURIComponent(organizationId)}/generation-api-access`,
  )
  if (!response.ok) throw await responseError(response, 'Failed to check Generation API access')
  return response.json()
}

export async function listGenerationApiKeys(organizationId: string): Promise<GenerationApiKeySummary[]> {
  const response = await authenticatedRequest(
    `${getApiUrl()}/organizations/${encodeURIComponent(organizationId)}/generation-api-keys`,
  )
  if (!response.ok) throw await responseError(response, 'Failed to load Generation API keys')
  const body = (await response.json()) as { apiKeys?: GenerationApiKeySummary[] }
  return body.apiKeys ?? []
}

export async function createGenerationApiKey(
  organizationId: string,
  label: string,
): Promise<{ key: string; apiKey: GenerationApiKeySummary }> {
  const response = await authenticatedRequest(
    `${getApiUrl()}/organizations/${encodeURIComponent(organizationId)}/generation-api-keys`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label }),
    },
  )
  if (!response.ok) throw await responseError(response, 'Failed to create Generation API key')
  return response.json()
}

/** The signed-in user's account keys: `martini login` approvals "as you", listed under personal Settings only. */
export async function listAccountGenerationApiKeys(): Promise<GenerationApiKeySummary[]> {
  const response = await authenticatedRequest(`${getApiUrl()}/me/generation-api-keys`)
  if (!response.ok) throw await responseError(response, 'Failed to load account keys')
  const body = (await response.json()) as { apiKeys?: GenerationApiKeySummary[] }
  return body.apiKeys ?? []
}

export async function revokeAccountGenerationApiKey(keyId: string): Promise<void> {
  const response = await authenticatedRequest(`${getApiUrl()}/me/generation-api-keys/${encodeURIComponent(keyId)}`, {
    method: 'DELETE',
  })
  if (!response.ok) throw await responseError(response, 'Failed to revoke account key')
}

export async function revokeGenerationApiKey(organizationId: string, keyId: string): Promise<void> {
  const response = await authenticatedRequest(
    `${getApiUrl()}/organizations/${encodeURIComponent(organizationId)}/generation-api-keys/${encodeURIComponent(keyId)}`,
    { method: 'DELETE' },
  )
  if (!response.ok) throw await responseError(response, 'Failed to revoke Generation API key')
}
